/**
 * DomeComponent - Manages a single reusable dome mesh for 360° images and videos
 * Uses texture swapping to efficiently switch between media without recreating geometry
 */

import { Logger } from '../utils/Logger';
import { Scene } from '@babylonjs/core/scene';

const logger = Logger.getLogger('DomeComponent');
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Material } from '@babylonjs/core/Materials/material';
import { VideoTexture } from '@babylonjs/core/Materials/Textures/videoTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { MediaItem } from '../media/MediaConfig';
import { MediaLoader } from '../media/MediaLoader';
import type { IAnalyticsService } from '../services/interfaces';
import type { SkyboxConfig } from '../types/config';

export class DomeComponent {
  private scene: Scene;
  private mediaLoader: MediaLoader;
  private parentNode: TransformNode | null = null;
  private domeMesh: Mesh | null = null;
  private domeMaterial: PBRMaterial | null = null;
  private currentTexture: Texture | VideoTexture | null = null;
  private currentMediaItem: MediaItem | null = null;
  private currentType: 'image' | 'video' | null = null;
  public analyticsService?: IAnalyticsService; // Injected from main.ts

  constructor(scene: Scene) {
    logger.debug('Constructor called');
    this.scene = scene;
    this.mediaLoader = new MediaLoader(scene);

    // CRITICAL: Dispose ALL existing dome meshes from previous instances (hot-reload cleanup)
    const existingDomes = this.scene.meshes.filter(m => m.name === 'dome');
    if (existingDomes.length > 0) {
      logger.warn('Found existing dome meshes from previous instance! Count:', existingDomes.length);
      logger.warn('  Disposing all existing domes (likely from hot-reload)...');
      existingDomes.forEach((mesh, index) => {
        logger.debug(`  Disposing dome ${index + 1}:`, mesh.uniqueId);
        mesh.dispose();
      });
    }

    this.createDomeMesh();
  }

  /**
   * Create the dome mesh (reusable for all media)
   */
  private createDomeMesh(): void {
    logger.debug('createDomeMesh() called');

    // Final safety check - should be impossible to reach this if constructor cleanup worked
    const existingDome = this.scene.getMeshByName('dome');
    if (existingDome) {
      logger.error('Dome mesh STILL exists after cleanup! Forcefully disposing...');
      existingDome.dispose();
    }

    // Create sphere mesh (inside-out for 360 viewing)
    this.domeMesh = CreateSphere(
      'dome',
      {
        diameter: 1000,
        segments: 64,
        sideOrientation: Mesh.BACKSIDE, // Inside-out
      },
      this.scene
    );

    // Make it infinite distance so it doesn't clip
    this.domeMesh.infiniteDistance = true;

    // CRITICAL: Force the dome to always be selected for rendering
    // This bypasses frustum culling which can incorrectly cull large meshes
    this.domeMesh.alwaysSelectAsActiveMesh = true;

    // Render in the first group (behind everything)
    this.domeMesh.renderingGroupId = 0;

    // Set default rotation (facing forward)
    this.domeMesh.rotation.y = Math.PI;

    // Create material (PBR unlit for true-to-source colors)
    this.domeMaterial = new PBRMaterial('domeMaterial', this.scene);
    this.domeMaterial.backFaceCulling = false;
    this.domeMaterial.unlit = true; // Unlit mode for accurate texture colors
    this.domeMaterial.transparencyMode = PBRMaterial.PBRMATERIAL_OPAQUE; // No transparency issues
    this.domeMaterial.forceDepthWrite = true; // Ensure depth is written for proper rendering

    this.domeMesh.material = this.domeMaterial;

    // Re-parent to stored parent node (persists across mesh recreation)
    if (this.parentNode) {
      this.domeMesh.parent = this.parentNode;
    }

    logger.debug('Dome mesh created:', this.domeMesh.uniqueId);
    logger.debug('Total dome meshes in scene:', this.scene.meshes.filter(m => m.name === 'dome').length);
  }

  /**
   * Set the parent node for the dome mesh (for skybox rotation grouping)
   */
  setParentNode(parent: TransformNode | null): void {
    this.parentNode = parent;
    if (this.domeMesh) {
      this.domeMesh.parent = parent;
    }
  }

  /**
   * Load media in background without applying to dome (for smooth transitions)
   * @param mediaItem Media item to load
   * @param type Type of media (image or video)
   * @param onProgress Optional progress callback
   * @param skyboxConfig Optional skybox config for multi-resolution support
   */
  async loadInBackground(
    mediaItem: MediaItem,
    type: 'image' | 'video',
    onProgress?: (percent: number) => void,
    skyboxConfig?: SkyboxConfig
  ): Promise<Texture | VideoTexture> {
    logger.debug(`Loading ${type} in background:`, mediaItem.title);

    try {
      let newTexture: Texture | VideoTexture;

      // Load new texture based on type
      if (type === 'image') {
        // Use variant-aware loader if skybox config is provided
        if (skyboxConfig && (skyboxConfig.variants || skyboxConfig.url)) {
          newTexture = await this.mediaLoader.loadImageWithVariants(skyboxConfig, mediaItem, onProgress);
        } else {
          // Fallback to standard image loader
          newTexture = await this.mediaLoader.loadImage(mediaItem, onProgress);
        }
      } else if (type === 'video') {
        // Check for HLS URL in skyboxConfig or mediaItem
        // This enables HLS streaming for: your Mux, user's Mux, or any HLS source
        const hlsUrl = skyboxConfig?.hlsUrl || mediaItem.hlsUrl;
        const mediaItemWithHLS: MediaItem = {
          ...mediaItem,
          hlsUrl: hlsUrl
        };

        // Use streaming loader (handles HLS with MP4 fallback)
        newTexture = await this.mediaLoader.loadStreamingVideo(mediaItemWithHLS, onProgress);
      } else {
        throw new Error(`Unknown media type: ${type}`);
      }

      logger.debug(`${type} loaded in background successfully:`, mediaItem.title);
      return newTexture;

    } catch (error) {
      logger.error(`Failed to load ${type} in background:`, error);
      throw error;
    }
  }

  /**
   * Swap to new texture (instant transition, old texture disposal handled separately)
   */
  swapTexture(newTexture: Texture | VideoTexture, mediaItem: MediaItem, type: 'image' | 'video'): void {
    logger.debug('swapTexture START - type:', type, 'title:', mediaItem.title);

    // Pre-swap texture analysis
    logger.debug('Input texture analysis:', {
      textureExists: !!newTexture,
      textureName: newTexture?.name,
      textureIsReady: newTexture?.isReady?.(),
      textureSize: newTexture?.getSize ? JSON.stringify(newTexture.getSize()) : 'N/A',
      textureUrl: mediaItem.url?.substring(0, 60) + '...',
      // Access internal texture for debugging (Babylon.js internal property)
      internalTextureExists: !!(newTexture as unknown as { _texture?: unknown })?._texture,
      internalTextureWidth: (newTexture as unknown as { _texture?: { width?: number } })?._texture?.width,
      internalTextureHeight: (newTexture as unknown as { _texture?: { height?: number } })?._texture?.height
    });

    // Ensure dome mesh exists and is visible
    if (!this.domeMesh) {
      logger.error('No dome mesh exists! Creating one...');
      this.createDomeMesh();
    }

    logger.debug('Dome mesh pre-swap state:', {
      meshExists: !!this.domeMesh,
      meshName: this.domeMesh?.name,
      meshUniqueId: this.domeMesh?.uniqueId,
      isVisible: this.domeMesh?.isVisible,
      isEnabled: this.domeMesh?.isEnabled(),
      materialName: this.domeMesh?.material?.name
    });

    // Ensure mesh is enabled and visible
    this.domeMesh!.setEnabled(true);
    this.domeMesh!.isVisible = true;
    // Note: isPickable is left as-is (default true for editor, disabled by UIManager for viewer)

    // CRITICAL: Force the mesh to be included in render (bypass all culling)
    this.domeMesh!.alwaysSelectAsActiveMesh = true;

    // Apply new texture to material immediately
    if (this.domeMaterial) {
      logger.debug('Applying texture to material...');

      // Store old texture reference for logging
      const oldTexture = this.domeMaterial.albedoTexture;
      logger.debug('Old albedoTexture:', oldTexture ? `${oldTexture.name} (ready: ${oldTexture.isReady()})` : 'NULL');

      this.domeMaterial.albedoTexture = newTexture;
      // Reset albedoColor to white to remove any color tint from previous solid color skybox
      this.domeMaterial.albedoColor = new Color3(1, 1, 1);
      // Ensure material is not frozen
      this.domeMaterial.unfreeze();
      // Force material to be opaque (no alpha blending issues)
      this.domeMaterial.transparencyMode = PBRMaterial.PBRMATERIAL_OPAQUE;
      // CRITICAL: Mark material as dirty to force re-evaluation
      this.domeMaterial.markAsDirty(Material.TextureDirtyFlag);

      logger.debug('Texture applied to dome material');
      logger.debug('Material state after apply:', {
        materialName: this.domeMaterial.name,
        unlit: this.domeMaterial.unlit,
        backFaceCulling: this.domeMaterial.backFaceCulling,
        transparencyMode: this.domeMaterial.transparencyMode,
        albedoColorR: this.domeMaterial.albedoColor.r,
        albedoColorG: this.domeMaterial.albedoColor.g,
        albedoColorB: this.domeMaterial.albedoColor.b,
        albedoTextureExists: !!this.domeMaterial.albedoTexture,
        albedoTextureReady: this.domeMaterial.albedoTexture?.isReady(),
        albedoTextureSize: this.domeMaterial.albedoTexture?.getSize ? JSON.stringify(this.domeMaterial.albedoTexture.getSize()) : 'N/A'
      });
    } else {
      logger.error('No dome material exists! This is a critical error.');
    }

    // CRITICAL: Force mesh world matrix computation to ensure proper positioning
    this.domeMesh!.computeWorldMatrix(true);

    // Update current references
    this.currentTexture = newTexture;
    this.currentType = type;

    // For video textures, ensure hlsUrl is preserved for recovery
    // The hlsUrl might be on the video source but not in the mediaItem
    if (type === 'video' && newTexture instanceof VideoTexture) {
      const videoSrc = newTexture.video.src || newTexture.video.currentSrc || '';
      const isHLS = videoSrc.includes('.m3u8');
      if (isHLS && !mediaItem.hlsUrl) {
        // Preserve the HLS URL for recovery purposes
        this.currentMediaItem = { ...mediaItem, hlsUrl: videoSrc };
        logger.debug('Preserved HLS URL for recovery:', videoSrc.substring(0, 60) + '...');
      } else {
        this.currentMediaItem = mediaItem;
      }
    } else {
      this.currentMediaItem = mediaItem;
    }

    // Debug: Final dome state
    logger.debug('Texture swap COMPLETE:', {
      domeVisible: this.domeMesh?.isVisible,
      domeEnabled: this.domeMesh?.isEnabled(),
      domePosition: this.domeMesh?.position.toString(),
      domeRotation: this.domeMesh?.rotation.toString(),
      domeScaling: this.domeMesh?.scaling.toString(),
      infiniteDistance: this.domeMesh?.infiniteDistance,
      renderingGroupId: this.domeMesh?.renderingGroupId,
      materialExists: !!this.domeMaterial,
      textureReady: newTexture.isReady(),
      alwaysSelectAsActiveMesh: this.domeMesh?.alwaysSelectAsActiveMesh
    });
  }

  /**
   * Properly dispose of a video texture with full cleanup
   * Also cleans up HLS streaming loaders if present
   */
  private async cleanupVideoTexture(texture: VideoTexture): Promise<void> {
    logger.debug('Cleaning up video texture...');
    const video = texture.video;

    // Get the texture ID (used for HLS loader tracking)
    const textureId = texture.name;

    // Pause video first
    video.pause();

    // Wait for video to be properly paused
    await new Promise<void>((resolve) => {
      if (video.paused) {
        resolve();
      } else {
        video.addEventListener('pause', () => resolve(), { once: true });
        setTimeout(resolve, 100); // Fallback timeout
      }
    });

    // Clean up HLS streaming loader if this video was using HLS
    // This destroys the hls.js instance and frees memory
    if (textureId) {
      this.mediaLoader.disposeStreamingVideo(textureId);
    }

    // Clear source and wait for 'emptied' event to ensure full cleanup
    video.src = '';
    await new Promise<void>((resolve) => {
      const onEmptied = () => {
        logger.debug('Video element emptied');
        resolve();
      };
      video.addEventListener('emptied', onEmptied, { once: true });

      // Call load() to trigger the emptied event
      video.load();

      // Fallback timeout in case event doesn't fire
      setTimeout(() => {
        video.removeEventListener('emptied', onEmptied);
        logger.debug('Video cleanup timeout - proceeding anyway');
        resolve();
      }, 500);
    });

    // Now safe to dispose
    texture.dispose();
    logger.debug('Video texture disposed safely');
  }

  /**
   * Dispose old texture after new one is applied (for smooth transitions)
   */
  async disposeOldTexture(oldTexture: Texture | VideoTexture | null, oldType: 'image' | 'video' | null): Promise<void> {
    if (!oldTexture) return;

    logger.debug(`Disposing old ${oldType} texture`);

    try {
      // If it was a video, do proper cleanup
      if (oldType === 'video' && oldTexture instanceof VideoTexture) {
        await this.cleanupVideoTexture(oldTexture);
      } else {
        // For images, simple dispose is fine
        oldTexture.dispose();
      }
      logger.debug('Old texture disposed successfully');
    } catch (error) {
      logger.error('Error disposing old texture:', error);
    }
  }

  /**
   * Load media (image or video) and swap texture
   * This is the legacy method - now internally uses loadInBackground + swapTexture for consistency
   */
  async load(mediaItem: MediaItem, type: 'image' | 'video'): Promise<void> {
    logger.info(`Loading ${type}:`, mediaItem.title);

    try {
      // Store reference to old texture for disposal
      const oldTexture = this.currentTexture;
      const oldType = this.currentType;

      // Clear material texture before disposing old one
      if (this.domeMaterial) {
        this.domeMaterial.albedoTexture = null;
      }

      // Load new texture
      const newTexture = await this.loadInBackground(mediaItem, type);

      // Swap to new texture
      this.swapTexture(newTexture, mediaItem, type);

      // Dispose old texture after swap
      await this.disposeOldTexture(oldTexture, oldType);

      logger.info(`${type} loaded successfully:`, mediaItem.title);

    } catch (error) {
      logger.error(`Failed to load ${type}:`, error);
      throw error;
    }
  }

  /**
   * Resume video playback from current position (for play/pause toggle)
   * Includes recovery logic for broken HLS MediaSource connections
   */
  async resume(): Promise<void> {
    if (this.currentType === 'video' && this.currentTexture instanceof VideoTexture) {
      const video = this.currentTexture.video;
      if (video.paused) {
        // Save current time before attempting to play (for recovery)
        const savedTime = video.currentTime;

        try {
          await video.play();
          logger.debug('Video resumed from', video.currentTime.toFixed(1), 'seconds');
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isMediaSourceError = errorMessage.includes('no supported source') ||
                                     errorMessage.includes('no source') ||
                                     errorMessage.includes('MEDIA_ERR') ||
                                     errorMessage.includes('Failed to load');

          if (isMediaSourceError && this.currentMediaItem) {
            logger.warn('HLS MediaSource broken, reloading video...');
            logger.debug('Saved position:', savedTime.toFixed(1), 'seconds');

            // Reload the video using the same retry logic as play()
            const hasHLS = !!this.currentMediaItem.hlsUrl;
            const hasMP4 = !!this.currentMediaItem.url;
            const hlsRetries = hasHLS ? 2 : 0;
            const mp4Retries = hasMP4 ? 1 : 0;
            const totalRetries = hlsRetries + mp4Retries;

            for (let retry = 1; retry <= totalRetries; retry++) {
              const useHLS = retry <= hlsRetries;
              logger.debug(`Recovery retry ${retry}/${totalRetries} - using ${useHLS ? 'HLS' : 'MP4'}...`);

              try {
                // Dispose the old streaming loader if exists
                this.mediaLoader.disposeStreamingVideo(this.currentMediaItem.id);

                // Small delay before retry to let resources clean up
                await new Promise(resolve => setTimeout(resolve, 300 * retry));

                let reloadedTexture: VideoTexture;
                if (useHLS) {
                  logger.debug('Retrying HLS:', this.currentMediaItem.hlsUrl);
                  reloadedTexture = await this.mediaLoader.loadStreamingVideo(this.currentMediaItem);
                } else {
                  logger.debug('Falling back to MP4:', this.currentMediaItem.url);
                  const fallbackMediaItem = { ...this.currentMediaItem, hlsUrl: undefined };
                  reloadedTexture = await this.mediaLoader.loadVideo(fallbackMediaItem, 0);
                }

                if (reloadedTexture) {
                  this.swapTexture(reloadedTexture, this.currentMediaItem, 'video');
                  const reloadedVideo = reloadedTexture.video;
                  if (reloadedVideo) {
                    reloadedVideo.muted = true; // Ensure muted for autoplay

                    // Restore playback position (with safety margin)
                    const duration = reloadedVideo.duration || 0;
                    if (savedTime > 0 && duration > 0 && savedTime < duration - 1) {
                      // Wait for video to be seekable
                      await new Promise<void>((resolve) => {
                        if (reloadedVideo.readyState >= 2) {
                          resolve();
                        } else {
                          reloadedVideo.addEventListener('loadeddata', () => resolve(), { once: true });
                          setTimeout(resolve, 2000); // Fallback timeout
                        }
                      });
                      reloadedVideo.currentTime = savedTime;
                      logger.debug('Restored position to:', savedTime.toFixed(1), 'seconds');
                    }

                    await reloadedVideo.play();
                    logger.info(`Video recovered and playing after ${useHLS ? 'HLS' : 'MP4'} retry ${retry}`);
                    return;
                  }
                }
              } catch (retryError) {
                logger.warn(`Recovery retry ${retry} failed:`, retryError);
                // Continue to next retry
              }
            }

            logger.error('All video recovery attempts failed');
          } else {
            logger.error('Failed to resume video:', error);
          }
        }
      }
    }
  }

  /**
   * Play video from beginning (only works if current media is video)
   * @returns true if video started playing successfully (including after recovery)
   */
  async play(): Promise<boolean> {
    if (this.currentType === 'video' && this.currentTexture instanceof VideoTexture) {
      const video = this.currentTexture.video;

      logger.debug('play() called - readyState:', video.readyState, 'paused:', video.paused);
      logger.debug('Video src:', video.src || '(none)', 'currentSrc:', video.currentSrc || '(none)');
      logger.debug('Network state:', video.networkState, 'error:', video.error?.message || '(none)');

      // Check if video has an error (e.g., demuxer error, decode error)
      const hasVideoError = video.error !== null;
      const hasNoSource = !video.src && !video.currentSrc && video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE;

      if (hasNoSource || hasVideoError) {
        logger.error('Video has error or no valid source:', video.error?.message || 'no source');

        // Try to reload the video with retries
        // Strategy: Try HLS twice, then fall back to MP4 if available
        if (this.currentMediaItem) {
          const hasHLS = !!this.currentMediaItem.hlsUrl;
          const hasMP4 = !!this.currentMediaItem.url;
          const hlsRetries = hasHLS ? 2 : 0;
          const mp4Retries = hasMP4 ? 1 : 0;
          const totalRetries = hlsRetries + mp4Retries;

          for (let retry = 1; retry <= totalRetries; retry++) {
            const useHLS = retry <= hlsRetries;
            logger.debug(`Retry ${retry}/${totalRetries} - using ${useHLS ? 'HLS' : 'MP4'}...`);

            try {
              // Dispose the old streaming loader if exists
              this.mediaLoader.disposeStreamingVideo(this.currentMediaItem.id);

              // Small delay before retry to let resources clean up
              await new Promise(resolve => setTimeout(resolve, 300 * retry));

              let reloadedTexture: VideoTexture;
              if (useHLS) {
                logger.debug('Retrying HLS:', this.currentMediaItem.hlsUrl);
                reloadedTexture = await this.mediaLoader.loadStreamingVideo(this.currentMediaItem);
              } else {
                logger.debug('Falling back to MP4:', this.currentMediaItem.url);
                const fallbackMediaItem = { ...this.currentMediaItem, hlsUrl: undefined };
                reloadedTexture = await this.mediaLoader.loadVideo(fallbackMediaItem, 0);
              }

              if (reloadedTexture) {
                this.swapTexture(reloadedTexture, this.currentMediaItem, 'video');
                const reloadedVideo = reloadedTexture.video;
                if (reloadedVideo) {
                  reloadedVideo.muted = true; // Ensure muted for autoplay
                  await reloadedVideo.play();
                  logger.info(`Video playing after ${useHLS ? 'HLS' : 'MP4'} retry ${retry}`);
                  return true;
                }
              }
            } catch (retryError) {
              logger.warn(`Retry ${retry} failed:`, retryError);
              // Continue to next retry
            }
          }

          logger.error('All video reload attempts failed');
        }
        return false;
      }

      // Wait for video to be ready if it's not already
      if (video.readyState < 2) { // HAVE_CURRENT_DATA or higher (lowered threshold)
        logger.debug('Waiting for video to be ready to play...');
        await new Promise<void>((resolve, reject) => {
          const onCanPlay = () => {
            logger.debug('Video canplay event fired');
            cleanup();
            resolve();
          };

          const onError = () => {
            logger.error('Video error while waiting:', video.error?.message);
            cleanup();
            reject(new Error(video.error?.message || 'Video load error'));
          };

          const cleanup = () => {
            video.removeEventListener('canplaythrough', onCanPlay);
            video.removeEventListener('error', onError);
          };

          video.addEventListener('canplaythrough', onCanPlay, { once: true });
          video.addEventListener('error', onError, { once: true });

          // Fallback timeout - but only resolve if video has a source
          setTimeout(() => {
            cleanup();
            if (video.src || video.currentSrc) {
              logger.debug('Timeout waiting for canplay, attempting to play anyway');
              resolve();
            } else {
              logger.error('Timeout and no valid source - rejecting');
              reject(new Error('Video has no source after timeout'));
            }
          }, 5000); // Increased to 5 seconds for slow HLS manifests
        });
      }

      // Now play the video from the beginning
      try {
        // Always restart from beginning when entering a video skybox scene
        video.currentTime = 0;
        logger.debug('Calling video.play() from beginning...');
        await video.play();
        logger.info('Video playing successfully');

        // Track video play
        this.analyticsService?.trackVideoPlay(
          this.analyticsService?.getCurrentStageIndex() || 0,
          this.currentMediaItem?.url
        );
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isNoSourceError = errorMessage.includes('no supported sources') ||
                                errorMessage.includes('no source') ||
                                errorMessage.includes('MEDIA_ERR') ||
                                errorMessage.includes('Failed to load');

        if (isNoSourceError && this.currentMediaItem) {
          logger.warn('HLS MediaSource broken during play(), attempting recovery...');

          // Track video error
          this.analyticsService?.trackVideoError(
            this.analyticsService?.getCurrentStageIndex() || 0,
            'Source error (attempting recovery): ' + errorMessage
          );

          // Retry logic - same as early detection path
          const hasHLS = !!this.currentMediaItem.hlsUrl;
          const hasMP4 = !!this.currentMediaItem.url;
          const hlsRetries = hasHLS ? 2 : 0;
          const mp4Retries = hasMP4 ? 1 : 0;
          const totalRetries = hlsRetries + mp4Retries;

          for (let retry = 1; retry <= totalRetries; retry++) {
            const useHLS = retry <= hlsRetries;
            logger.debug(`Recovery retry ${retry}/${totalRetries} - using ${useHLS ? 'HLS' : 'MP4'}...`);

            try {
              // Dispose the old streaming loader if exists
              this.mediaLoader.disposeStreamingVideo(this.currentMediaItem.id);

              // Small delay before retry to let resources clean up
              await new Promise(resolve => setTimeout(resolve, 300 * retry));

              let reloadedTexture: VideoTexture;
              if (useHLS) {
                logger.debug('Retrying HLS:', this.currentMediaItem.hlsUrl);
                reloadedTexture = await this.mediaLoader.loadStreamingVideo(this.currentMediaItem);
              } else {
                logger.debug('Falling back to MP4:', this.currentMediaItem.url);
                const fallbackMediaItem = { ...this.currentMediaItem, hlsUrl: undefined };
                reloadedTexture = await this.mediaLoader.loadVideo(fallbackMediaItem, 0);
              }

              if (reloadedTexture) {
                this.swapTexture(reloadedTexture, this.currentMediaItem, 'video');
                const reloadedVideo = reloadedTexture.video;
                if (reloadedVideo) {
                  reloadedVideo.muted = true; // Ensure muted for autoplay
                  await reloadedVideo.play();
                  logger.info(`Video recovered and playing after ${useHLS ? 'HLS' : 'MP4'} retry ${retry}`);
                  return true;
                }
              }
            } catch (retryError) {
              logger.warn(`Recovery retry ${retry} failed:`, retryError);
              // Continue to next retry
            }
          }

          logger.error('All video recovery attempts failed');
          return false;
        }

        logger.error('Video autoplay blocked:', error);
        logger.debug('User interaction required to play video - click anywhere to start');

        // Track video error
        this.analyticsService?.trackVideoError(
          this.analyticsService?.getCurrentStageIndex() || 0,
          'Autoplay blocked: ' + errorMessage
        );

        // Setup click handler to play on user interaction (only for autoplay block errors)
        const playOnClick = () => {
          video.currentTime = 0; // Restart from beginning
          video.play().then(() => {
            logger.info('Video started after user interaction (from beginning)');
            window.removeEventListener('click', playOnClick);
          }).catch(playError => {
            logger.error('Play after click failed:', playError);
          });
        };
        window.addEventListener('click', playOnClick, { once: true });
        return false; // Autoplay blocked, waiting for user interaction
      }
    } else {
      logger.debug('play() called but not a video or texture not ready');
    }
    return false;
  }

  /**
   * Pause video
   */
  pause(): void {
    if (this.currentType === 'video' && this.currentTexture instanceof VideoTexture) {
      this.currentTexture.video.pause();
      logger.debug('Video paused');

      // Track video pause
      this.analyticsService?.trackVideoPause(
        this.analyticsService?.getCurrentStageIndex() || 0,
        this.currentTexture.video.currentTime
      );
    }
  }

  /**
   * Pause video and mute audio immediately
   * Used during scene transitions to prevent audio overlap
   * This is separate from pause() to avoid analytics tracking during transitions
   */
  stopVideoAudio(): void {
    if (this.currentType === 'video' && this.currentTexture instanceof VideoTexture) {
      const video = this.currentTexture.video;
      video.pause();
      video.muted = true; // Also mute to be extra safe
      logger.debug('Video audio stopped (paused + muted)');
    }
  }

  /**
   * Toggle play/pause
   */
  togglePlayPause(): void {
    if (this.currentType === 'video' && this.currentTexture instanceof VideoTexture) {
      if (this.currentTexture.video.paused) {
        this.play();
      } else {
        this.pause();
      }
    }
  }

  /**
   * Check if video is playing
   */
  isPlaying(): boolean {
    if (this.currentType === 'video' && this.currentTexture instanceof VideoTexture) {
      return !this.currentTexture.video.paused;
    }
    return false;
  }

  /**
   * Set rotation of dome mesh around Y axis (vertical axis - spins horizontally)
   */
  setRotation(angleRadians: number): void {
    if (this.domeMesh) {
      // Rotate around Y axis - positive = counter-clockwise from above
      this.domeMesh.rotation.y = Math.PI + angleRadians;
      logger.debug(`Dome mesh rotation.y set to: ${Math.PI + angleRadians} radians (${angleRadians} offset from default)`);
    }
  }

  /**
   * Set absolute rotation (used when transferring from preview dome)
   */
  setAbsoluteRotation(angleRadians: number): void {
    if (this.domeMesh) {
      this.domeMesh.rotation.y = angleRadians;
      logger.debug(`Dome mesh rotation.y set to absolute: ${angleRadians} radians (${(angleRadians * 180 / Math.PI).toFixed(1)}°)`);
    }
  }

  /**
   * Rotate dome by angle (relative rotation around Y axis)
   */
  rotate(angleRadians: number): void {
    if (this.domeMesh) {
      this.domeMesh.rotation.y += angleRadians;
    }
  }

  /**
   * Reset rotation to default
   */
  resetRotation(): void {
    if (this.domeMesh) {
      this.domeMesh.rotation.y = Math.PI;
    }
  }

  /**
   * Get the dome mesh (for parenting under a TransformNode)
   */
  getDomeMesh(): Mesh | null {
    return this.domeMesh;
  }

  /**
   * Get current media item
   */
  getCurrentMediaItem(): MediaItem | null {
    return this.currentMediaItem;
  }

  /**
   * Get current media type
   */
  getCurrentType(): 'image' | 'video' | null {
    return this.currentType;
  }

  /**
   * Get current texture (for cache comparison)
   */
  getCurrentTexture(): Texture | VideoTexture | null {
    return this.currentTexture;
  }

  /**
   * Get video element (if current media is video)
   */
  getVideoElement(): HTMLVideoElement | null {
    if (this.currentType === 'video' && this.currentTexture instanceof VideoTexture) {
      return this.currentTexture.video;
    }
    return null;
  }

  /**
   * Get current time (for videos)
   */
  getCurrentTime(): number {
    const video = this.getVideoElement();
    return video?.currentTime || 0;
  }

  /**
   * Get duration (for videos)
   */
  getDuration(): number {
    const video = this.getVideoElement();
    return video?.duration || 0;
  }

  /**
   * Seek video to time (0-1 normalized)
   */
  seek(normalizedTime: number): void {
    const video = this.getVideoElement();
    if (video) {
      video.currentTime = normalizedTime * video.duration;
    }
  }

  /**
   * Set volume (for videos)
   */
  setVolume(volume: number): void {
    const video = this.getVideoElement();
    if (video) {
      video.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Get volume (for videos)
   */
  getVolume(): number {
    const video = this.getVideoElement();
    return video?.volume || 1;
  }

  /**
   * Set dome to a solid color (no texture)
   */
  setSolidColor(hexColor: string): void {
    logger.debug(`Setting solid color: ${hexColor}`);

    // Clear any existing textures
    if (this.currentTexture) {
      if (this.currentType === 'video' && this.currentTexture instanceof VideoTexture) {
        // Clean up HLS streaming loader if present
        const textureId = this.currentTexture.name;
        if (textureId) {
          this.mediaLoader.disposeStreamingVideo(textureId);
        }
        this.currentTexture.video.pause();
        this.currentTexture.video.src = '';
      }
      this.currentTexture.dispose();
      this.currentTexture = null;
    }

    // Apply solid color to material
    if (this.domeMaterial) {
      this.domeMaterial.albedoTexture = null;

      // Convert hex to Color3
      const color = Color3.FromHexString(hexColor);
      this.domeMaterial.albedoColor = color;

      logger.debug('Solid color applied:', color);
    }

    this.currentMediaItem = null;
    this.currentType = null;
  }

  /**
   * Unload current media (but keep dome mesh)
   */
  async unload(): Promise<void> {
    // Clear material first
    if (this.domeMaterial) {
      this.domeMaterial.albedoTexture = null;
      this.domeMaterial.albedoColor = Color3.Black();
    }

    // Properly dispose current texture
    await this.disposeOldTexture(this.currentTexture, this.currentType);
    this.currentTexture = null;

    this.currentMediaItem = null;
    this.currentType = null;
  }

  /**
   * Dispose all resources including dome mesh
   */
  async dispose(): Promise<void> {
    await this.unload();

    if (this.domeMaterial) {
      this.domeMaterial.dispose();
      this.domeMaterial = null;
    }

    if (this.domeMesh) {
      this.domeMesh.dispose();
      this.domeMesh = null;
    }
  }
}
